import { useState, useEffect, useRef } from 'react';
import { useSearchStore } from '../../stores/search-store.js';
import { api } from '../../lib/api-client.js';

const inputClass =
  'bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors w-full';

interface SetResult {
  code: string;
  name: string;
}

interface SelectedSet {
  code: string;
  name: string;
}

export function SetInput() {
  const setUserInput = useSearchStore((s) => s.setUserInput);
  // Watch the advanced filter `set` field — when cleared externally (Clear All), reset chips
  const storeSet = useSearchStore((s) => s.advancedFilters.set);

  const [selectedSets, setSelectedSets] = useState<SelectedSet[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<SetResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // When store's set field is cleared (e.g. by Clear All), reset local chips
  useEffect(() => {
    if (storeSet === '' && selectedSets.length > 0) {
      setSelectedSets([]);
      setInputValue('');
    }
  }, [storeSet, selectedSets.length]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const syncSetsToBar = (sets: SelectedSet[]) => {
    const bar = useSearchStore.getState().userInput;
    // Remove existing e: clauses and (e:X OR e:Y) groups
    let cleaned = bar.replace(/\(e:\S+(?:\s+OR\s+e:\S+)*\)/g, '').replace(/e:\S+/g, '').replace(/\s{2,}/g, ' ').trim();
    if (sets.length === 0) {
      setUserInput(cleaned);
    } else if (sets.length === 1) {
      setUserInput(cleaned ? `${cleaned} e:${sets[0]!.code}` : `e:${sets[0]!.code}`);
    } else {
      const group = `(${sets.map((s) => `e:${s.code}`).join(' OR ')})`;
      setUserInput(cleaned ? `${cleaned} ${group}` : group);
    }
    // Keep store in sync (store just tracks the first set code for simple cases)
    useSearchStore.getState().setAdvancedFilter('set', sets[0]?.code ?? '');
  };

  const fetchSets = (q: string) => {
    if (q.length < 2) { setSuggestions([]); return; }
    api.getCatalogSets(q).then((res) => {
      const selectedCodes = new Set(selectedSets.map((s) => s.code));
      setSuggestions(res.sets.filter((s) => !selectedCodes.has(s.code)));
    }).catch(() => {/* ignore */});
  };

  const handleInput = (val: string) => {
    setInputValue(val);
    setIsOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSets(val), 200);
  };

  const selectSet = (s: SetResult) => {
    const next = [...selectedSets, { code: s.code, name: s.name }];
    setSelectedSets(next);
    syncSetsToBar(next);
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
  };

  const removeSet = (code: string) => {
    const next = selectedSets.filter((s) => s.code !== code);
    setSelectedSets(next);
    syncSetsToBar(next);
  };

  return (
    <div className="space-y-1" ref={containerRef}>
      <label className="text-[10px] text-gray-500 uppercase tracking-wider">Set</label>

      {selectedSets.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {selectedSets.map((s) => (
            <span
              key={s.code}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-800 border border-gray-700 rounded text-[10px] text-gray-300"
            >
              <span className="text-gray-500">{s.code.toUpperCase()}</span>
              {s.name}
              <button
                onClick={() => removeSet(s.code)}
                className="text-gray-600 hover:text-red-400 ml-0.5"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => { if (inputValue.length >= 2) setIsOpen(true); }}
          placeholder={selectedSets.length > 0 ? 'Add another set...' : 'Search sets...'}
          className={inputClass}
        />
        {isOpen && suggestions.length > 0 && (
          <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-40 overflow-y-auto">
            {suggestions.map((s) => (
              <button
                key={s.code}
                onClick={() => selectSet(s)}
                className="block w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 transition-colors"
              >
                <span className="text-gray-500 mr-1.5">{s.code.toUpperCase()}</span>
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
